import React, { useEffect, useMemo, useRef, useState } from 'react';
import { FiPlus, FiX, FiTrash2 } from 'react-icons/fi';
import { BASE_URL } from '../constants/config';
import LeagueChecklist from './LeagueChecklist';
import {
    MAX_PARTNER_LOGO_BYTES,
    PARTNER_NAME_MAX,
    PARTNER_DESCRIPTION_MAX,
    validatePartnerForm,
    normalizeHref,
    sameIdSet,
} from '../utilities/partners';
import '../styles/editModal.css';
import '../styles/leagueManagement.css';
import '../styles/partners.css';

// Создание и правка спонсора.
// props:
// isOpen, onClose
// partner - спонсор из fetchAdminPartners или null для нового
// groups - чемпионаты с лигами (utilities/partners: leagueGroupsFrom)
// initialLeagueIds - лиги, отмеченные у нового спонсора (создание со страницы лиги)
// saving - идёт запрос
// onSave({name, description, href, logoFile, leagueIds, fieldsChanged, leaguesChanged}) -> Promise<bool>
// onDelete(partner) -> Promise<bool>

const LOGO_TYPES = ['image/png', 'image/jpeg', 'image/webp'];

const resolveImage = (image) => {
    if (!image) return '';
    return image.startsWith('http') || image.startsWith('data:') ? image : BASE_URL.url + image;
};

export default function PartnerModal({
    isOpen, onClose, partner, groups, initialLeagueIds, saving, onSave, onDelete,
}) {
    const [form, setForm] = useState({ name: '', description: '', href: '' });
    const [logoFile, setLogoFile] = useState(null);
    const [logoPreview, setLogoPreview] = useState('');
    const [logoError, setLogoError] = useState(null);
    const [leagueIds, setLeagueIds] = useState([]);
    const [submitted, setSubmitted] = useState(false);
    const [confirmDelete, setConfirmDelete] = useState(false);
    const fileInputRef = useRef(null);

    const originalLeagueIds = useMemo(
        () => (partner ? partner.leagues.map(link => link.league_id) : []),
        [partner]
    );

    // Форма заполняется при каждом открытии; initialLeagueIds берём на момент открытия
    useEffect(() => {
        if (!isOpen) return;
        setForm({
            name: partner ? partner.name : '',
            description: partner ? partner.description : '',
            href: partner ? partner.href : '',
        });
        setLogoFile(null);
        setLogoError(null);
        setSubmitted(false);
        setConfirmDelete(false);
        setLeagueIds(partner ? partner.leagues.map(link => link.league_id) : (initialLeagueIds || []));
        if (fileInputRef.current) fileInputRef.current.value = '';
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen, partner]);

    useEffect(() => {
        if (!logoFile) {
            setLogoPreview('');
            return undefined;
        }
        const url = URL.createObjectURL(logoFile);
        setLogoPreview(url);
        return () => URL.revokeObjectURL(url);
    }, [logoFile]);

    if (!isOpen) return null;

    const image = logoPreview || resolveImage(partner && partner.image);
    const errors = validatePartnerForm(form, Boolean(image));
    const fieldsChanged = !partner
        || Boolean(logoFile)
        || form.name.trim() !== partner.name
        || form.description.trim() !== partner.description
        || normalizeHref(form.href) !== partner.href;
    const leaguesChanged = !sameIdSet(leagueIds, originalLeagueIds);
    const dirty = !partner || fieldsChanged || leaguesChanged;

    const setField = (field, value) => setForm(prev => ({ ...prev, [field]: value }));

    const pickFile = (e) => {
        const file = e.target.files && e.target.files[0];
        e.target.value = '';
        if (!file) return;
        if (!LOGO_TYPES.includes(file.type)) {
            setLogoError('Нужна картинка PNG, JPG или WebP.');
            return;
        }
        if (file.size > MAX_PARTNER_LOGO_BYTES) {
            setLogoError('Файл больше 1 МБ — выберите поменьше.');
            return;
        }
        setLogoError(null);
        setLogoFile(file);
    };

    const submit = async () => {
        setSubmitted(true);
        if (Object.keys(errors).length > 0) return;
        const ok = await onSave({
            ...form,
            logoFile,
            leagueIds,
            fieldsChanged,
            leaguesChanged,
        });
        if (ok) onClose();
    };

    const remove = async () => {
        const ok = await onDelete(partner);
        if (ok) onClose();
    };

    const fieldError = (key) => (submitted && errors[key]
        ? <span className="fieldError">{errors[key]}</span>
        : null);

    return (
        <div className="modalOverlay" onClick={saving ? undefined : onClose}>
            <div className="modalContent modalContentWide ptModal" onClick={(e) => e.stopPropagation()}>
                <div className="modalHeader">
                    <h2>{partner ? 'Спонсор' : 'Новый спонсор'}</h2>
                    <button className="modalClose" onClick={onClose} disabled={saving}>×</button>
                </div>

                <div className="modalBody modalBodyScroll">
                    <div className="ptFormTop">
                        <div className="lmRow ptLogoColumn">
                            <label className="modalLabel">Логотип</label>
                            <input
                                ref={fileInputRef}
                                type="file"
                                accept="image/png,image/jpeg,image/webp"
                                className="logoSquareInput"
                                onChange={pickFile}
                            />
                            {image ? (
                                <div className="logoSquare logoSquareFilled ptLogoSquare">
                                    <img src={image} alt="" />
                                    <button
                                        type="button"
                                        className="logoSquareHit"
                                        onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                    >
                                        Сменить
                                    </button>
                                    {logoFile && (
                                        <button
                                            type="button"
                                            className="logoSquareRemove"
                                            aria-label="Вернуть прежний логотип"
                                            onClick={() => setLogoFile(null)}
                                        >
                                            <FiX />
                                        </button>
                                    )}
                                </div>
                            ) : (
                                <button
                                    type="button"
                                    className="logoSquare"
                                    onClick={() => fileInputRef.current && fileInputRef.current.click()}
                                >
                                    <FiPlus />
                                    <span>Добавить логотип</span>
                                </button>
                            )}
                            {logoError && <span className="fieldError">{logoError}</span>}
                            {!logoError && fieldError('logo')}
                            <span className="fieldHint">PNG, JPG или WebP до 1 МБ.</span>
                        </div>

                        <div className="ptFormFields">
                            <div className="lmRow">
                                <label className="modalLabel">Название</label>
                                <input
                                    type="text"
                                    className="modalInput"
                                    maxLength={PARTNER_NAME_MAX}
                                    value={form.name}
                                    placeholder="Например, «Чайная роза»"
                                    autoFocus={!partner}
                                    onChange={(e) => setField('name', e.target.value)}
                                />
                                {fieldError('name')}
                            </div>
                            <div className="lmRow">
                                <label className="modalLabel">Описание</label>
                                <input
                                    type="text"
                                    className="modalInput"
                                    maxLength={PARTNER_DESCRIPTION_MAX}
                                    value={form.description}
                                    placeholder="Например, «Генеральный партнёр»"
                                    onChange={(e) => setField('description', e.target.value)}
                                />
                                <span className="fieldHint">
                                    Необязательно. {form.description.length} из {PARTNER_DESCRIPTION_MAX} символов.
                                </span>
                                {fieldError('description')}
                            </div>
                        </div>
                    </div>

                    <div className="lmRow">
                        <label className="modalLabel">Ссылка</label>
                        <input
                            type="text"
                            className="modalInput"
                            value={form.href}
                            placeholder="https://example.ru"
                            onChange={(e) => setField('href', e.target.value)}
                        />
                        <span className="fieldHint">
                            Откроется по нажатию на логотип. Можно оставить пустым.
                        </span>
                        {fieldError('href')}
                    </div>

                    <div className="lmRow">
                        <label className="modalLabel">Где показывать</label>
                        <span className="fieldHint ptChecklistHint">
                            Спонсор появится на страницах отмеченных лиг. Порядок спонсоров
                            внутри лиги задаётся в настройках лиги, на вкладке «Спонсоры».
                        </span>
                        <LeagueChecklist
                            groups={groups}
                            selected={leagueIds}
                            onChange={setLeagueIds}
                            disabled={saving}
                        />
                    </div>
                </div>

                <div className="modalFooter ptFooter">
                    {partner && (confirmDelete ? (
                        <div className="ptDeleteConfirm">
                            <span>Удалить спонсора и убрать его из всех лиг?</span>
                            <button className="modalButton ptDangerButton" onClick={remove} disabled={saving}>
                                Удалить
                            </button>
                            <button
                                className="modalButton modalButtonCancel"
                                onClick={() => setConfirmDelete(false)}
                                disabled={saving}
                            >
                                Нет
                            </button>
                        </div>
                    ) : (
                        <button
                            type="button"
                            className="ptDeleteLink"
                            onClick={() => setConfirmDelete(true)}
                            disabled={saving}
                        >
                            <FiTrash2 /> Удалить спонсора
                        </button>
                    ))}
                    {!confirmDelete && (
                        <div className="ptFooterMain">
                            <button className="modalButton modalButtonCancel" onClick={onClose} disabled={saving}>
                                Отмена
                            </button>
                            <button
                                className="modalButton modalButtonSave"
                                onClick={submit}
                                disabled={saving || !dirty}
                            >
                                {saving ? 'Сохраняем…' : (partner ? 'Сохранить' : 'Создать спонсора')}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
}
